import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, AbstractControl, Validators, ValidationErrors, ValidatorFn, FormControl } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { formatDisplayText } from '../../utils/text-case.utils';
import { ConfigService } from '../../shared-services/config.service';

@Component({
  selector: 'app-filter-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonToggleModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './filter-dialog.component.html',
  styleUrls: ['./filter-dialog.component.scss']
})
export class FilterDialogComponent implements OnInit {
  private static readonly DOMAIN_ATTRIBUTE_OPERATORS = new Set([
    'Is equal',
    'Is not equal',
    'is_null',
    'is_not_null',
  ]);

  queryForm!: FormGroup;
  filterEntity = '';
  filterPayload: any = {};
  defaultApplyPayload: any = null;
  domainAttributesOptions: { [key: string]: any[] } = {};
  domainAttributes: any[] = [];
  domainAttributesLoading: { [key: string]: boolean } = {};
  private loadedDomainAttributes = new Set<string>();
  private readonly fieldSearchControls = new WeakMap<AbstractControl, FormControl<string | null>>();
  private readonly operatorSearchControls = new WeakMap<AbstractControl, FormControl<string | null>>();
  private readonly valueSearchControls = new WeakMap<AbstractControl, FormControl<string | null>>();

  fields = [
    { value: 'code', viewValue: 'Code' },
    { value: 'name', viewValue: 'Name' },
    { value: 'category', viewValue: 'Category' },
    { value: 'price', viewValue: 'Price' }
  ];

  operators = [
    { value: 'Is equal', viewValue: 'Is equal' },
    { value: 'Is not equal ', viewValue: 'Is not equal to' },
    { value: 'in', viewValue: 'In' },
    { value: 'not_in', viewValue: 'Not In' },
    { value: 'contains', viewValue: 'Contains' },
    { value: 'greater than', viewValue: 'Greater than' },
    { value: 'less than', viewValue: 'Less than' },
    { value: 'is_null', viewValue: 'Is Null' },
    { value: 'is_not_null', viewValue: 'Is Not Null' },
    { value: 'greater than or equal', viewValue: 'Is Greater Than Or Equal' },
    { value: 'less than or equal', viewValue: 'Is Less Than Or Equal' }

  ];

  constructor(
    public dialogRef: MatDialogRef<FilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.filterEntity = this.data?.filterEntity || this.data?.filterPayload?.entity || '';
    this.filterPayload = this.data?.filterPayload || {};
    this.defaultApplyPayload = this.data?.defaultApplyPayload || null;
    const payloadFields = this.getPayloadFields();
    if (payloadFields.length > 0) {
      this.fields = payloadFields;
    } else if (Array.isArray(this.data?.tableColumns) && this.data.tableColumns.length > 0) {
      // UI Standards (Point 3): enforce Capital Case on field names for consistency
      this.fields = this.data.tableColumns
        .filter((col: any) => col && col.key)
        .map((col: any) => ({
          value: col.key,
          viewValue: formatDisplayText(col.label || col.key, 'capital'),
        }));
    }
    this.queryForm = this.createRuleSetGroup();
    this.restoreAppliedFilters();
    this.loadFilterFields();
  }

  private getPayloadFields(): { value: string; viewValue: string }[] {
    const attributesList = this.filterPayload?.attributesList;
    if (!Array.isArray(attributesList)) {
      return [];
    }

    return attributesList
      .filter((attribute: any) => typeof attribute === 'string' && attribute.trim())
      .map((attribute: string) => ({
        value: attribute,
        viewValue: formatDisplayText(attribute, 'capital'),
      }));
  }

  private loadFilterFields(): void {
    if (!this.data?.filterApiUrl || !this.filterPayload || typeof this.filterPayload !== 'object') {
      return;
    }

    const requestConfig = {
      customPayload: this.filterPayload,
      skipDefaultPayload: true,
      headers: this.data?.filterHeaders,
    };

    this.configService.getApi(
      this.data.filterApiUrl,
      { requestParams: {} },
      requestConfig,
      (this.data?.filterMethod || 'post').toLowerCase()
    ).subscribe({
    next: (response: any) => {
        const responseFields = this.getResponseFields(response);
        if (responseFields.length > 0) {
          this.fields = responseFields;
        }
        
        // Extract domain attributes for additional API calls
        this.extractDomainAttributes(response);
        this.preloadDomainOptionsForCurrentRules();
        
      },
      error: (error) => console.error('Error loading filter fields:', error),
    });
  }

  private getResponseFields(response: any): { value: string; viewValue: string }[] {
    const entityKey = this.filterPayload?.requestData?.entity || this.filterEntity;
    const entityConfig =
      response?.responseList?.[0]?.[entityKey] ||
      response?.[entityKey] ||
      response?.data?.[entityKey] ||
      [];
    const entityRows = Array.isArray(entityConfig) ? entityConfig : [entityConfig];
    const attributes = entityRows.flatMap((row: any) => [
      ...(Array.isArray(row?.freeFormAttributes) ? row.freeFormAttributes : []),
      ...(Array.isArray(row?.domainAttributes) ? row.domainAttributes : []),
    ]);

    return attributes
      .map((attribute: any) => this.mapAttributeField(attribute))
      .filter((field: { value: string; viewValue: string } | null): field is { value: string; viewValue: string } => !!field);
  }

  private mapAttributeField(attribute: any): { value: string; viewValue: string } | null {
    if (typeof attribute === 'string') {
      const trimmedAttribute = attribute.trim();
      return trimmedAttribute
        ? { value: trimmedAttribute, viewValue: formatDisplayText(trimmedAttribute, 'capital') }
        : null;
    }

    if (!attribute || typeof attribute !== 'object') {
      return null;
    }

    // Handle domain attributes (e.g., { attribute: "region", entity: "p_pricing_region" })
    const value = attribute.attribute ?? attribute.name ?? attribute.attributeName ?? attribute.key ?? attribute.value;
    if (!value) {
      return null;
    }

    const label = attribute.display_name ?? attribute.displayName ?? attribute.label ?? attribute.name ?? attribute.attribute ?? value;
    return {
      value: String(value),
      viewValue: formatDisplayText(String(label), 'capital'),
    };
  }

  private extractDomainAttributes(response: any): void {
    const entityKey = this.filterPayload?.requestData?.entity || this.filterEntity;
    const entityConfig =
      response?.responseList?.[0]?.[entityKey] ||
      response?.[entityKey] ||
      response?.data?.[entityKey] ||
      [];
    const entityRows = Array.isArray(entityConfig) ? entityConfig : [entityConfig];
    const allDomainAttributes = entityRows.flatMap((row: any) =>
      Array.isArray(row?.domainAttributes) ? row.domainAttributes : [],
    );

    this.domainAttributes = allDomainAttributes.filter((attribute: any, index: number, list: any[]) => {
      const attributeName = this.getDomainAttributeName(attribute);
      return !!attributeName && index === list.findIndex((item) => this.getDomainAttributeName(item) === attributeName);
    });
  }

  onFieldSelectionChange(ruleGroup: AbstractControl): void {
    const selectedField = String(ruleGroup.get('field')?.value || '').trim();
    ruleGroup.get('value')?.reset(null, { emitEvent: false });
    this.getRuleValueSearchControl(ruleGroup).setValue('', { emitEvent: false });

    if (selectedField && this.isDomainAttribute(selectedField)) {
      this.resetRuleOperatorIfInvalid(ruleGroup);
      this.loadDomainAttributeOptions(selectedField);
      return;
    }

    this.resetRuleOperatorIfInvalid(ruleGroup);
  }

  isDomainAttribute(fieldName: string): boolean {
    const resolvedField = String(fieldName || '').trim();
    return this.domainAttributes.some((attribute: any) => this.getDomainAttributeName(attribute) === resolvedField);
  }

  isDomainAttributeLoading(fieldName: string | null | undefined): boolean {
    const resolvedField = String(fieldName || '').trim();
    return !!resolvedField && !!this.domainAttributesLoading[resolvedField];
  }

  getDomainAttributeOptions(fieldName: string | null | undefined): any[] {
    const resolvedField = String(fieldName || '').trim();
    return resolvedField ? this.domainAttributesOptions[resolvedField] || [] : [];
  }

  getRuleFieldSearchControl(ruleGroup: AbstractControl): FormControl<string | null> {
    return this.getOrCreateSearchControl(this.fieldSearchControls, ruleGroup);
  }

  getRuleOperatorSearchControl(ruleGroup: AbstractControl): FormControl<string | null> {
    return this.getOrCreateSearchControl(this.operatorSearchControls, ruleGroup);
  }

  getRuleValueSearchControl(ruleGroup: AbstractControl): FormControl<string | null> {
    return this.getOrCreateSearchControl(this.valueSearchControls, ruleGroup);
  }

  getFilteredFields(ruleGroup: AbstractControl): { value: string; viewValue: string }[] {
    const term = this.normalizeSearchTerm(this.getRuleFieldSearchControl(ruleGroup).value);
    if (!term) {
      return this.fields;
    }

    return this.fields.filter((field) => this.matchesSearchTerm([field.value, field.viewValue], term));
  }

  getFilteredOperators(ruleGroup: AbstractControl): { value: string; viewValue: string }[] {
    const availableOperators = this.getAvailableOperators(ruleGroup);
    const term = this.normalizeSearchTerm(this.getRuleOperatorSearchControl(ruleGroup).value);
    if (!term) {
      return availableOperators;
    }

    return availableOperators.filter((operator) => this.matchesSearchTerm([operator.value, operator.viewValue], term));
  }

  getFilteredDomainAttributeOptions(ruleGroup: AbstractControl): any[] {
    const fieldName = String(ruleGroup.get('field')?.value || '').trim();
    const options = this.getDomainAttributeOptions(fieldName);
    const term = this.normalizeSearchTerm(this.getRuleValueSearchControl(ruleGroup).value);
    if (!term) {
      return options;
    }

    return options.filter((option: any) => this.matchesSearchTerm([option?.value, option?.label], term));
  }

  private loadDomainAttributeOptions(attributeName: string): void {
    if (!attributeName || !this.data?.domainApiUrl || !this.data?.domainPayloadTemplate) {
      return;
    }

    if (this.loadedDomainAttributes.has(attributeName) || this.domainAttributesLoading[attributeName]) {
      return;
    }

    this.domainAttributesLoading[attributeName] = true;

    const domainPayload = JSON.parse(JSON.stringify(this.data.domainPayloadTemplate));
    domainPayload.requestData = {
      ...(domainPayload.requestData || {}),
      ...(this.data?.selectedFilters || {}),
      entity: this.getSelectedEntity(),
      attribute: attributeName,
    };

    const requestConfig = {
      customPayload: domainPayload,
      skipDefaultPayload: true,
      headers: this.data?.filterHeaders,
    };

    this.configService
      .getApi(
        this.data.domainApiUrl,
        { requestParams: {} },
        requestConfig,
        (this.data?.filterMethod || 'post').toLowerCase(),
      )
      .subscribe({
        next: (response: any) => {
          this.domainAttributesOptions[attributeName] = this.extractOptionsFromResponse(response, attributeName);
          this.loadedDomainAttributes.add(attributeName);
          this.domainAttributesLoading[attributeName] = false;
        },
        error: (err) => {
          console.error(`Error loading domain attribute options for ${attributeName}:`, err);
          this.domainAttributesOptions[attributeName] = [];
          this.domainAttributesLoading[attributeName] = false;
        },
      });
  }

  private getSelectedEntity(): string {
    const candidates = [
      this.data?.selectedEntity,
      this.data?.filterEntity,
      this.data?.filterPayload?.requestData?.entity,
      this.data?.filterPayload?.entity,
      this.filterEntity,
    ];

    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined) {
        continue;
      }

      const resolved = typeof candidate === 'object'
        ? candidate?.value ?? candidate?.name ?? candidate?.display_name ?? candidate?.displayName
        : candidate;

      const normalized = String(resolved || '').trim();
      if (normalized) {
        return normalized;
      }
    }

    return '';
  }

  private extractOptionsFromResponse(response: any, attributeName: string): any[] {
    const options =
      response?.responseList?.[0]?.[attributeName] ||
      response?.responseList?.[0]?.data?.[attributeName] ||
      response?.[attributeName] ||
      response?.data?.[attributeName] ||
      response?.options?.[attributeName] ||
      response?.options ||
      response?.data ||
      [];

    const rawOptions = Array.isArray(options) ? options : [];
    return rawOptions
      .map((option: any) => this.normalizeDomainOption(option))
      .filter((option: any): option is { label: string; value: any } => !!option);
  }

  private normalizeDomainOption(option: any): { label: string; value: any } | null {
    if (option === null || option === undefined) {
      return null;
    }

    if (typeof option !== 'object') {
      const value = String(option);
      return {
        label: formatDisplayText(value, 'capital'),
        value,
      };
    }

    const data = option?.data ?? option;
    const code = data?.code ?? option?.code;
    const name =
      data?.name ??
      option?.name ??
      data?.display_name ??
      data?.displayName ??
      option?.display_name ??
      option?.displayName;
    const label =
      data?.code ??
      option?.code ??
      option?.label ??
      data?.id ??
      option?.id ??
      data?.display_name ??
      data?.displayName ??
      option?.name ??
      option?.value;
    const value =
      data?.id ??
      option?.id ??
      data?.code ??
      option?.code ??
      data?.value ??
      option?.value ??
      label;

    if (label === undefined || value === undefined) {
      return null;
    }

    const displayLabel = code && name && String(code) !== String(name)
      ? `${code}{${name}}`
      : formatDisplayText(String(label), 'capital');

    return {
      label: displayLabel,
      value,
    };
  }

  private getDomainAttributeName(attribute: any): string {
    if (typeof attribute === 'string') {
      return attribute.trim();
    }

    if (!attribute || typeof attribute !== 'object') {
      return '';
    }

    return String(
      attribute.attribute ??
      attribute.attributeName ??
      attribute.key ??
      attribute.name ??
      attribute.value ??
      '',
    ).trim();
  }

  createRuleSetGroup(): FormGroup {
    return this.fb.group({
      condition: ['AND'],
      rules: this.fb.array([])
    }, {
      validators: [this.requireAtLeastOneRuleValidator()],
    });
  }

  createRuleGroup(): FormGroup {
    const ruleGroup = this.fb.group({
      field: ['', Validators.required],
      operator: ['', Validators.required],
      value: ['', Validators.required]
    });

    this.configureRuleValueBehavior(ruleGroup);
    return ruleGroup;
  }

  getRules(formGroup: AbstractControl): FormArray {
    return formGroup.get('rules') as FormArray;
  }

  addRule(formGroup: AbstractControl): void {
    this.getRules(formGroup).push(this.createRuleGroup());
  }

  addRuleSet(formGroup: AbstractControl): void {
    this.getRules(formGroup).push(this.createRuleSetGroup());
  }

  removeRule(formGroup: AbstractControl, index: number): void {
    this.getRules(formGroup).removeAt(index);
    this.refreshRulesetValidity(formGroup);
  }

  private requireAtLeastOneRuleValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rules = control.get('rules');
      if (!(rules instanceof FormArray)) {
        return null;
      }

      return rules.length > 0 ? null : { requiredRule: true };
    };
  }

  private refreshRulesetValidity(control: AbstractControl | null): void {
    if (!control) {
      return;
    }

    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });

    const parent = control.parent;
    if (parent) {
      this.refreshRulesetValidity(parent);
    }
  }

  isRuleSet(control: AbstractControl): boolean {
    return !!control.get('rules');
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onApply(): void {
    if (this.queryForm.valid) {
      const payload = this.buildApplyPayload(this.queryForm);
      Object.defineProperty(payload, '__appliedFilterSummaryLines', {
        value: this.buildAppliedFilterSummaryLines(this.queryForm),
        enumerable: false,
      });
      console.log('Formatted Query:', payload);
      this.dialogRef.close(payload);
    } else {
      this.queryForm.markAllAsTouched();
    }
  }

  private buildApplyPayload(group: AbstractControl): any {
    const formValue = group.getRawValue();
    const filters = [
      ...this.getDefaultApplyFilters(),
      ...this.transformRules(formValue.rules, formValue.condition),
    ];
    return {
      requestData: {
        entity: this.getSelectedEntity(),
        filters,
      },
      responseList: [],
      entity: this.data?.entity || 'product',
      workflowId: this.data?.workflowId || 'UI',
      endpoints: [
        {
          functionName: this.data?.submitFunctionName || 'GenericDynamicReadServices',
          version: this.data?.version || '1.0',
        },
      ],
      communicationType: 'rest',
      version: this.data?.version || '1.0',
      functionName: this.data?.submitFunctionName || 'GenericDynamicReadServices',
    };
  }

  private getDefaultApplyFilters(): any[] {
    const defaults = this.defaultApplyPayload?.requestData?.filters;
    return Array.isArray(defaults) ? JSON.parse(JSON.stringify(defaults)) : [];
  }

  private transformRules(rules: any[], parentCondition: string): any[] {
    if (!Array.isArray(rules) || rules.length === 0) {
      return [];
    }

    const filters: any[] = [];

    rules.forEach((rule, index) => {
      if (rule.rules) {
        const nestedFilters = this.transformRules(rule.rules, rule.condition);
        if (nestedFilters.length > 0) {
          nestedFilters[0].beginScope = this.appendScopeMarker(nestedFilters[0].beginScope, '(');
          nestedFilters[nestedFilters.length - 1].endScope = this.appendScopeMarker(
            nestedFilters[nestedFilters.length - 1].endScope,
            ')',
          );

          if (index < rules.length - 1) {
            nestedFilters[nestedFilters.length - 1].logicalOperator = parentCondition;
          }

          filters.push(...nestedFilters);
        }
        return;
      }

      const operator = this.mapOperator(rule.operator);
      filters.push({
        attributeName: rule.field,
        operator,
        attributeValue: this.normalizeRuleValue(rule.value, operator),
        attributeType: '',
        logicalOperator: index < rules.length - 1 ? parentCondition : '',
        beginScope: '',
        endScope: '',
      });
    });

    return filters;
  }

  private appendScopeMarker(existingScope: any, marker: '(' | ')'): string {
    const scope = String(existingScope || '');
    return marker === '(' ? `${scope}(` : `${scope})`;
  }

  private mapOperator(op: string): string {
    const mapping: { [key: string]: string } = {
      'equal': 'is_equal_to',
      'is_equal_to': 'is_equal_to',
      'not_equal': 'is_not_equal_to',
      'is_not_equal_to': 'is_not_equal_to',
      'in': 'in',
      'not_in': 'not_in',
      'contains': 'contains',
      'greater_than': 'greater_than',
      'less_than': 'less_than',
      'is_null': 'is_null',
      'is_not_null': 'is_not_null'
    };
    return mapping[op] || String(op || '').trim();
  }

  private normalizeRuleValue(value: any, operator: string): any {
    if (this.isNullValueOperator(operator)) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.length ? value : null;
    }

    if (['in', 'not_in'].includes(String(operator || '').trim()) && typeof value === 'string' && value.trim() !== '') {
      const items = value.split(',').map((item) => item.trim()).filter((item) => item !== '');
      if (!items.length) {
        return null;
      }
      const allNumeric = items.every((item) => item !== '' && !Number.isNaN(Number(item)));
      return allNumeric ? items.map(Number) : items;
    }

    return value === '' ? null : value;
  }

  public isNullValueOperator(operator: string): boolean {
    return ['is_null', 'is_not_null'].includes(String(operator || '').trim());
  }

  public shouldShowValueField(ruleGroup: AbstractControl): boolean {
    return !this.isNullValueOperator(ruleGroup.get('operator')?.value);
  }

  private buildAppliedFilterSummaryLines(group: AbstractControl): string[] {
    const formValue = group.getRawValue();
    return this.transformRulesForSummary(formValue.rules, formValue.condition);
  }

  private transformRulesForSummary(rules: any[], parentCondition: string): string[] {
    if (!Array.isArray(rules) || rules.length === 0) {
      return [];
    }

    const lines: string[] = [];
    rules.forEach((rule, index) => {
      const suffix = index < rules.length - 1 ? ` ${String(parentCondition || 'and').toLowerCase()}` : '';

      if (rule.rules) {
        const nestedLines = this.transformRulesForSummary(rule.rules, rule.condition);
        if (nestedLines.length > 0) {
          nestedLines[0] = `(${nestedLines[0]}`;
          nestedLines[nestedLines.length - 1] = `${nestedLines[nestedLines.length - 1]})${suffix}`;
          lines.push(...nestedLines);
        }
        return;
      }

      lines.push(`${this.getFilterFieldSummaryLabel(rule.field)} ${this.getFilterOperatorSummaryLabel(rule.operator)} ${this.getFilterValueSummaryLabel(rule.field, rule.value)}${suffix}`.trim());
    });

    return lines;
  }

  private getFilterFieldSummaryLabel(field: any): string {
    const fieldValue = String(field || '').trim();
    const fieldConfig = this.fields.find((item) => item.value === fieldValue);
    return fieldConfig?.viewValue || formatDisplayText(fieldValue, 'capital');
  }

  private getFilterOperatorSummaryLabel(operator: any): string {
    const operatorValue = String(operator || '').trim();
    const operatorConfig = this.operators.find((item) => item.value === operatorValue);
    return operatorConfig?.viewValue || formatDisplayText(operatorValue, 'capital');
  }

  private getFilterValueSummaryLabel(field: any, value: any): string {
    if (Array.isArray(value)) {
      return value.map((item) => this.getFilterValueSummaryLabel(field, item)).join(', ');
    }

    const fieldName = String(field || '').trim();
    const option = this.getDomainAttributeOptions(fieldName).find((item: any) => item?.value === value);
    if (option?.label) {
      return String(option.label);
    }

    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  }

  private restoreAppliedFilters(): void {
    const appliedFilters = this.getRestorableAppliedFilters();
    if (!appliedFilters.length) {
      return;
    }

    const rules = this.getRules(this.queryForm);
    rules.clear();
    this.queryForm.patchValue({
      condition: this.resolveRestoredCondition(appliedFilters),
    }, { emitEvent: false });

    appliedFilters.forEach((filter: any) => {
      const ruleGroup = this.createRuleGroup();
      ruleGroup.patchValue({
        field: filter?.attributeName ?? '',
        operator: this.mapPayloadOperatorToFormOperator(filter?.operator),
        value: filter?.attributeValue ?? '',
      }, { emitEvent: false });
      rules.push(ruleGroup);
    });
  }

  private getRestorableAppliedFilters(): any[] {
    const appliedPayloadFilters = this.data?.appliedFilterPayload?.requestData?.filters;
    const filters = Array.isArray(appliedPayloadFilters) ? appliedPayloadFilters : [];
    if (!filters.length) {
      return [];
    }

    const defaultFilters = this.getDefaultApplyFilters();
    return filters.filter((filter: any) => !defaultFilters.some((defaultFilter: any) => this.areFiltersEquivalent(filter, defaultFilter)));
  }

  private areFiltersEquivalent(left: any, right: any): boolean {
    return JSON.stringify(left || {}) === JSON.stringify(right || {});
  }

  private resolveRestoredCondition(filters: any[]): string {
    const logicalOperator = filters.find((filter: any) => String(filter?.logicalOperator || '').trim())?.logicalOperator;
    const normalized = String(logicalOperator || 'AND').trim().toUpperCase();
    return normalized === 'OR' ? 'OR' : 'AND';
  }

  private mapPayloadOperatorToFormOperator(operator: any): string {
    const normalized = String(operator || '').trim();
    const mapping: { [key: string]: string } = {
      is_equal_to: 'Is equal',
      is_not_equal_to: 'Is not equal',
      in: 'in',
      not_in: 'not_in',
      contains: 'contains',
      greater_than: 'greater than',
      less_than: 'less than',
      is_null: 'is_null',
      is_not_null: 'is_not_null'
    };

    return mapping[normalized] || normalized;
  }

  private getAvailableOperators(ruleGroup: AbstractControl): { value: string; viewValue: string }[] {
    const fieldName = String(ruleGroup.get('field')?.value || '').trim();
    if (!this.isDomainAttribute(fieldName)) {
      return this.operators;
    }

    return this.operators.filter((operator) =>
      FilterDialogComponent.DOMAIN_ATTRIBUTE_OPERATORS.has(String(operator?.value || '').trimEnd()),
    );
  }

  private resetRuleOperatorIfInvalid(ruleGroup: AbstractControl): void {
    const operatorControl = ruleGroup.get('operator');
    if (!operatorControl) {
      return;
    }

    const selectedOperator = String(operatorControl.value || '').trimEnd();
    if (!selectedOperator) {
      return;
    }

    const isStillValid = this.getAvailableOperators(ruleGroup).some((operator) =>
      String(operator?.value || '').trimEnd() === selectedOperator,
    );

    if (!isStillValid) {
      operatorControl.reset('', { emitEvent: false });
    }
  }

  private configureRuleValueBehavior(ruleGroup: FormGroup): void {
    const operatorControl = ruleGroup.get('operator');
    const valueControl = ruleGroup.get('value');
    if (!operatorControl || !valueControl) {
      return;
    }

    const updateValueValidator = (operator: any): void => {
      if (this.isNullValueOperator(operator)) {
        valueControl.reset('', { emitEvent: false });
        valueControl.clearValidators();
      } else {
        valueControl.setValidators([Validators.required]);
      }

      valueControl.updateValueAndValidity({ emitEvent: false });
    };

    updateValueValidator(operatorControl.value);
    operatorControl.valueChanges.subscribe((operator) => updateValueValidator(operator));
  }

  private preloadDomainOptionsForCurrentRules(): void {
    if (!this.queryForm) {
      return;
    }

    this.collectRuleGroups(this.queryForm).forEach((ruleGroup) => {
      const fieldName = String(ruleGroup.get('field')?.value || '').trim();
      if (fieldName && this.isDomainAttribute(fieldName)) {
        this.loadDomainAttributeOptions(fieldName);
      }
    });
  }

  private collectRuleGroups(formGroup: AbstractControl): AbstractControl[] {
    const rules = this.getRules(formGroup);
    if (!rules) {
      return [];
    }

    return rules.controls.flatMap((control) => this.isRuleSet(control) ? this.collectRuleGroups(control) : [control]);
  }

  private getOrCreateSearchControl(
    store: WeakMap<AbstractControl, FormControl<string | null>>,
    ruleGroup: AbstractControl,
  ): FormControl<string | null> {
    const existingControl = store.get(ruleGroup);
    if (existingControl) {
      return existingControl;
    }

    const createdControl = new FormControl<string | null>('');
    store.set(ruleGroup, createdControl);
    return createdControl;
  }

  private normalizeSearchTerm(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private matchesSearchTerm(values: any[], searchTerm: string): boolean {
    return values.some((value) => String(value || '').toLowerCase().includes(searchTerm));
  }
}
